/**
 * Sidebar dropdown: items with sub-menus toggle on parent link click (preventDefault).
 * Items without sub-menus behave as normal links.
 * Expects: container with .container__item, .container__link, .container__sub; --expanded on item when open.
 */
export interface SidebarDropdownOptions {
  itemClass?: string;
  linkClass?: string;
  subClass?: string;
  expandedClass?: string;
}

const DEFAULT_OPTIONS: Required<SidebarDropdownOptions> = {
  itemClass: 'comm-sidebar__item',
  linkClass: 'comm-sidebar__link',
  subClass: 'comm-sidebar__sub',
  expandedClass: '--expanded',
};

export class SidebarDropdown {
  private readonly container: HTMLElement;
  private readonly opts: Required<SidebarDropdownOptions>;
  private boundHandlers = new Map<HTMLElement, (e: Event) => void>();

  constructor(container: string | HTMLElement, options: SidebarDropdownOptions = {}) {
    const el =
      typeof container === 'string'
        ? document.querySelector<HTMLElement>(container)
        : container;
    if (!el) {
      throw new Error('SidebarDropdown: container not found');
    }
    this.container = el;
    this.opts = { ...DEFAULT_OPTIONS, ...options };
  }

  init(): void {
    const { itemClass, linkClass, subClass, expandedClass } = this.opts;
    const itemSel = '.' + itemClass.trim().split(/\s+/).join('.');
    const items = this.container.querySelectorAll<HTMLElement>(itemSel);
    items.forEach((item) => {
      const sub = item.querySelector('.' + subClass.trim().split(/\s+/).join('.'));
      const link = item.querySelector<HTMLAnchorElement>('.' + linkClass.trim().split(/\s+/).join('.'));
      if (!link) return;

      if (sub) {
        const handler = (e: Event): void => {
          e.preventDefault();
          item.classList.toggle(expandedClass);
        };
        link.addEventListener('click', handler);
        this.boundHandlers.set(link, handler);
      }
      // No sub → link works normally (navigate)
    });
  }

  destroy(): void {
    const { linkClass, expandedClass } = this.opts;
    this.boundHandlers.forEach((handler, link) => {
      link.removeEventListener('click', handler);
    });
    this.boundHandlers.clear();
  }
}
